import {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactElement,
  forwardRef,
} from 'react';
import cn from 'classnames';

import styles from './HotspotDot.module.scss';

export interface HotspotDotProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Position as a percentage of the uncapped image frame, 0-100 — `y` is
      passed to CSS as the unitless `--y` (not a `top`), which
      `HotspotDot.module.scss` maps onto the viewport-capped box (see
      `frameMapping.ts`). */
  x: number;
  y: number;
  /** Accessible name — the product's title. */
  label: string;
}

/**
 * A single hotspot marker positioned over the lifestyle image. Click/tap only
 * on every device — no hover-to-open (SPEC.md decision 7). Rendered as a
 * `HotspotPopover`'s `Popover.Trigger` (`render` prop) — open/close, click,
 * and `aria-*` all come through via the spread rest props rather than a
 * custom `isOpen`/`onToggle` pair; `data-popup-open` (set by Base UI while
 * its popover is open) drives the open styling, same convention as
 * `QuickAddTrigger.module.scss`'s `.cta[data-popup-open]`.
 */
const HotspotDot = forwardRef<HTMLButtonElement, HotspotDotProps>(
  ({ x, y, label, className, style, ...rest }, ref): ReactElement => (
    <button
      type="button"
      ref={ref}
      className={cn(styles.dot, className)}
      style={{ left: `${x}%`, '--y': y, ...style } as CSSProperties}
      aria-label={label}
      {...rest}
    >
      <span className={styles.dotInner} aria-hidden="true" />
    </button>
  )
);

HotspotDot.displayName = 'HotspotDot';

export default HotspotDot;
